import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { apiClient } from '@/lib/api'

interface LocationModalProps {
  isOpen: boolean
  onClose: () => void
  location: any | null
  tenantId: string
  onSuccess: () => void
}

export function LocationModal({ isOpen, onClose, location, tenantId, onSuccess }: LocationModalProps) {
  const [formData, setFormData] = useState({
    location_id: '',
    name: '',
    address: '',
    phone: ''
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (location) {
      setFormData({
        location_id: location.location_id || '',
        name: location.name || '',
        address: location.address || '',
        phone: location.phone || ''
      })
    } else {
      setFormData({ location_id: '', name: '', address: '', phone: '' })
    }
  }, [location, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      setSaving(true)
      
      if (location) {
        const { location_id, ...updateData } = formData
        await apiClient.updateLocation(tenantId, location_id, updateData)
      } else {
        await apiClient.createLocation(tenantId, formData)
      }
      
      onSuccess()
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to save location'
      alert(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{location ? 'Edit Location' : 'Add Location'}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="location_id">Location ID *</Label>
            <Input
              id="location_id"
              value={formData.location_id}
              onChange={(e) => {
                const sanitized = e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/g, '')
                setFormData(prev => ({ ...prev, location_id: sanitized }))
              }}
              placeholder="main-office"
              required
              disabled={!!location}
            />
            <p className="text-xs text-gray-500">Auto-formatted: lowercase, spaces→underscores (cannot be changed after creation)</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Main Office"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              placeholder="123 Main St"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="+15551234567"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : location ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
